import { SearchOutlined } from '@ant-design/icons';
import { CLIENT_TOOL_GROUPS, CLIENT_TOOLS, OVERVIEW_KEY, SESSIONS_KEY, clientToolOf, type ClientToolGroup } from '@cc/schema';
import { useTranslate } from '@refinedev/core';
import { Button, Select, Tooltip, Typography } from 'antd';
import { createStyles } from 'antd-style';
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { GROUP_LABEL_KEYS, toolLabel } from '../../components/clientTools/labels';
import {
  ROW_ONE,
  firstSectionOfGroup,
  rowOneOf,
  rowTwoClusters,
  rowTwoOf,
  type ClientSection,
  type NavItem,
  type RowTool,
} from './clientPageModel';

/**
 * E57 · the client page's navigation (founder Q-pia-1 D; Q-e57g1-1b A; D-nav-fixes-2026-10-19: Q-nav-3 B, Q-nav-5 B,
 * Q-nav-6 A) — cc-practice, screen "Client page — navigation (groups and tools)", e57e0001-…-055.
 *
 * Row 1: Overview · Sessions & notes · Plan · Track · Formulation · Skills & exercises · Safety, and Find a tool at its end.
 * Row 2: the chosen group's tools as pills that WRAP onto more lines — never folded behind "⋯", so nothing is hidden at any
 * width, in English or Russian (Q-nav-3 B). The SAME pills in the canonical order for every client, as My tools leaves
 * them; every pill looks the same (N-3, founder Q-e57h-2 C, 2026-10-22: no dimming, no tooltip — "in use" stays as data-in-use) — names only, no count and no "new" (Q-nav-9); a
 * tool the therapist hid that this client uses stays, marked "hidden by you". Skills & exercises carries four small labels
 * between its tools — Cognitive · Behavioral · Values & motivation · Mindfulness (Q-nav-5 B). No "Use a tool" (Q-nav-6 A:
 * each tool's own Create does it).
 *
 * Behavior lines implemented here:
 *   · gesture: choosing a group shows its tools; choosing a tool opens its section in the same page (clientPage.group.*);
 *   · format: every client sees the same pills in the same order; every tool looks the same, in use or not —
 *     names only, no count and no "new" on any tab (founder Q-nav-9, 2026-10-19);
 *   · format: a hidden tool this client uses shows "hidden by you" (clientPage.tools.hiddenByYou);
 *   · format: row 2 wraps; Skills & exercises shows its small labels (clientPage.skillsSubgroup.*);
 *   · gesture: Find a tool filters the names across all groups (clientPage.tools.find);
 *   · empty-state: a group whose tools are all hidden shows a line linking to Settings › My tools (clientPage.tools.groupEmpty).
 * No badge or count anywhere in this navigation (Q-pia-4 A; Q-nav-9).
 */
const useStyles = createStyles(({ token, css }) => ({
  rowOne: css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    column-gap: 32px;
    border-bottom: 1px solid ${token.colorBorderSecondary};
    position: relative;
    /* Find a tool never sits alone on a line: when the tabs and the field do not fit one line it becomes an icon, then
       (still not fitting) the tabs' gap tightens — measured on the real DOM (fitFind), never guessed from the width */
    &[data-find='icon'] .cc-find-slot {
      width: 32px;
    }
    &[data-tight='true'] {
      column-gap: 18px;
    }
    &[data-tight='tighter'] {
      column-gap: 12px;
      & > [role='tab'] {
        font-size: 14px;
      }
    }
  `,
  tab: css`
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 12px 0;
    margin: 0;
    border: 0;
    background: none;
    font: inherit;
    font-size: 15px;
    line-height: 24px;
    color: ${token.colorTextSecondary};
    cursor: pointer;
    white-space: nowrap;
    &:hover {
      color: ${token.colorPrimary};
    }
    &:focus-visible {
      outline: 2px solid ${token.colorPrimaryBorder};
      outline-offset: 2px;
      border-radius: 4px;
    }
  `,
  tabOn: css`
    color: ${token.colorPrimary};
    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: -1px;
      height: 2px;
      background: ${token.colorPrimary};
    }
  `,
  rowOneEnd: css`
    margin-left: auto;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: 170px;
    padding: 6px 0;
    position: relative;
  `,
  // the opened field floats over the right end of row 1 — it never changes the row's size
  findOpen: css`
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    z-index: 6;
    width: 230px;
    background: ${token.colorBgLayout};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    border-radius: 6px;
  `,
  rowTwo: css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    padding: 8px 0 4px;
  `,
  pill: css`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 30px;
    padding: 0 12px;
    margin: 0;
    border: 1px solid transparent;
    border-radius: 999px;
    background: #ebe7e4;
    color: ${token.colorText};
    font: inherit;
    font-size: 14px;
    line-height: 1;
    white-space: nowrap;
    cursor: pointer;
    &:hover {
      border-color: ${token.colorPrimaryBorderHover};
    }
    &:focus-visible {
      outline: 2px solid ${token.colorPrimaryBorder};
      outline-offset: 2px;
    }
  `,
  pillOn: css`
    background: ${token.colorPrimary};
    color: #fff;
    &:hover {
      border-color: ${token.colorPrimary};
    }
  `,
  hidden: css`
    display: inline-flex;
    align-items: center;
    height: 18px;
    padding: 0 6px;
    border-radius: 999px;
    background: #ebe7e4;
    color: ${token.colorTextSecondary};
    font-size: 11px;
  `,
  // a small label never ends a line on its own: it is held together with the first pill it names
  lead: css`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-left: 8px;
    white-space: nowrap;
    &:first-child {
      margin-left: 0;
    }
  `,
  subLabel: css`
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: ${token.colorTextTertiary};
    font-weight: 600;
    margin-right: 2px;
  `,
}));

export const ClientToolNav: React.FC<{
  clientId: string;
  section: ClientSection;
  nav: readonly NavItem[] | null;
  hiddenTools: readonly string[];
  onNavigate: (section: ClientSection) => void;
}> = ({ section, nav, hiddenTools, onNavigate }) => {
  const t = useTranslate();
  const { styles, cx } = useStyles();
  const activeOne = rowOneOf(section);
  const rowOneRef = useRef<HTMLDivElement>(null);
  const [findMode, setFindMode] = useState<'field' | 'icon'>('field');
  const [findOpen, setFindOpen] = useState(false);

  // Find a tool's fit: the full field when the tabs and it fit ONE line; else an icon; else the icon with tighter tab gaps.
  // Only data attributes change here (no React state in the measuring), React then renders the same mode.
  const fitFind = useCallback(() => {
    const row = rowOneRef.current;
    const slot = row?.querySelector<HTMLElement>('.cc-find-slot');
    if (!row || !slot) return;
    const tabs = Array.from(row.querySelectorAll<HTMLElement>(':scope > [role="tab"]'));
    if (!tabs.length) return;
    const bad = () => {
      const s = slot.getBoundingClientRect();
      const alone = !tabs.some((tab) => {
        const b = tab.getBoundingClientRect();
        return b.top < s.bottom - 1 && s.top < b.bottom - 1;
      });
      const wraps = new Set(tabs.map((tab) => Math.round(tab.getBoundingClientRect().top))).size > 1;
      return alone || wraps;
    };
    row.dataset.find = 'field';
    row.dataset.tight = 'false';
    let mode: 'field' | 'icon' = 'field';
    if (bad()) {
      row.dataset.find = 'icon';
      mode = 'icon';
      if (bad()) {
        row.dataset.tight = 'true';
        if (bad()) row.dataset.tight = 'tighter';
      }
    }
    setFindMode((prev) => (prev === mode ? prev : mode));
  }, []);
  useLayoutEffect(() => {
    const el = rowOneRef.current;
    if (!el) return undefined;
    fitFind();
    const ro = new ResizeObserver(() => fitFind());
    ro.observe(el);
    // the tabs' text changes with the language and the web font arriving
    const mo = new MutationObserver(() => fitFind());
    mo.observe(el, { childList: true, characterData: true, subtree: true });
    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, [fitFind]);
  const openTool = section.kind === 'tool' ? section.tool : null;

  const rows = useMemo(() => {
    const out = {} as Record<ClientToolGroup, RowTool[]>;
    for (const g of CLIENT_TOOL_GROUPS) out[g] = rowTwoOf(g, nav, hiddenTools, openTool);
    return out;
  }, [nav, hiddenTools, openTool]);

  const onRowOne = (key: string) => {
    if (key === OVERVIEW_KEY) return onNavigate({ kind: 'overview', view: 'digest' });
    if (key === SESSIONS_KEY) return onNavigate({ kind: 'sessions' });
    const g = key as ClientToolGroup;
    onNavigate(firstSectionOfGroup(g, rows[g]));
  };

  const rowOneTab = (key: string, testId: string, label: React.ReactNode) => (
    <button
      key={key}
      type="button"
      role="tab"
      aria-selected={activeOne === key}
      className={cx(styles.tab, activeOne === key && styles.tabOn)}
      onClick={() => onRowOne(key)}
      data-testid={testId}
    >
      {label}
    </button>
  );

  const rowOneItems = ROW_ONE.map((key) => {
    if (key === OVERVIEW_KEY) return rowOneTab(key, 'cc-client-show-tab-overview', t('clientPage.group.overview'));
    if (key === SESSIONS_KEY) return rowOneTab(key, 'cc-client-show-tab-sessions', t('clientPage.group.sessions'));
    const g = key as ClientToolGroup;
    return rowOneTab(key, `cc-client-show-group-${g}`, t(GROUP_LABEL_KEYS[g]));
  });

  // ── row 2: pills that wrap ──
  const pill = (r: RowTool) => {
    const on = r.tool === openTool;
    return (
      <button
        key={r.tool}
        type="button"
        role="tab"
        aria-selected={on}
        className={cx(styles.pill, on && styles.pillOn)}
        onClick={() => {
          const def = clientToolOf(r.tool);
          if (def) onNavigate({ kind: 'tool', tool: def.key, group: def.group });
        }}
        data-testid={`cc-client-show-tab-${r.testId}`}
        data-in-use={r.inUse ? 'true' : 'false'}
        data-hidden-by-me={r.hiddenByMe ? 'true' : 'false'}
      >
        {toolLabel(t, r.tool)}
        {r.hiddenByMe ? (
          <span className={styles.hidden} data-testid={`cc-client-show-tab-${r.testId}-hidden`}>
            {t('clientPage.tools.hiddenByYou')}
          </span>
        ) : null}
      </button>
    );
  };

  let rowTwo: React.ReactNode = null;
  if (section.kind === 'overview') {
    const view = (v: 'digest' | 'details', key: string) => (
      <button
        type="button"
        role="tab"
        aria-selected={section.view === v}
        className={cx(styles.pill, section.view === v && styles.pillOn)}
        onClick={() => onNavigate({ kind: 'overview', view: v })}
        data-testid={`cc-client-show-overview-${v}`}
      >
        {t(key)}
      </button>
    );
    rowTwo = (
      <div role="tablist" className={styles.rowTwo} data-testid="cc-client-show-overview-row">
        {view('digest', 'clientPage.overview.digest')}
        {view('details', 'clientPage.overview.details')}
      </div>
    );
  } else if (section.kind === 'tool' || section.kind === 'group') {
    const row = rows[section.group];
    rowTwo = row.length ? (
      <div role="tablist" className={styles.rowTwo} data-testid="cc-client-show-tool-row">
        {rowTwoClusters(section.group, row).map((c) => (
          <React.Fragment key={c.subgroup ?? 'rest'}>
            {c.subgroup ? (
              <span className={styles.lead}>
                <span className={styles.subLabel} data-testid={`cc-client-show-subgroup-${c.subgroup}`}>
                  {t(`clientPage.skillsSubgroup.${c.subgroup}`)}
                </span>
                {pill(c.tools[0])}
              </span>
            ) : (
              pill(c.tools[0])
            )}
            {c.tools.slice(1).map(pill)}
          </React.Fragment>
        ))}
      </div>
    ) : (
      <Typography.Paragraph type="secondary" data-testid="cc-client-show-group-empty" style={{ margin: '8px 0' }}>
        {t('clientPage.tools.groupEmpty')}{' '}
        <Link to="/settings/my-tools" data-testid="cc-client-show-group-empty-link">
          {t('clientPage.tools.groupEmptyLink')}
        </Link>
      </Typography.Paragraph>
    );
  }

  // Find a tool: every tool by name, across all groups (a hidden one too — it opens its section, the board's deep link)
  const findOptions = CLIENT_TOOL_GROUPS.map((g) => ({
    label: t(GROUP_LABEL_KEYS[g]),
    options: CLIENT_TOOLS.filter((d) => d.group === g).map((d) => ({ value: d.key, label: toolLabel(t, d.key) })),
  }));


  const findSelect = (extra: Record<string, unknown>) => (
      <Select
        size="small"
        showSearch
        allowClear
        value={null}
        placeholder={
          <span>
            <SearchOutlined /> {t('clientPage.tools.find')}
          </span>
        }
        style={{ width: '100%' }}
        popupMatchSelectWidth={260}
        options={findOptions}
        optionFilterProp="label"
        onSelect={(tool: string | null) => {
          setFindOpen(false);
          const def = clientToolOf(tool);
          if (def) onNavigate({ kind: 'tool', tool: def.key, group: def.group });
        }}
        data-testid="cc-client-show-find-tool"
        {...extra}
      />
  );

  return (
    <nav data-testid="cc-client-show-tabs" aria-label={t('clientPage.navAria')}>
      <div role="tablist" className={styles.rowOne} data-testid="cc-client-show-row-one" ref={rowOneRef}>
        {rowOneItems}
        <div className={`${styles.rowOneEnd} cc-find-slot`} data-testid="cc-client-show-find-slot">
          {findMode === 'field' ? (
            findSelect({})
          ) : findOpen ? (
            <div className={styles.findOpen}>
              {findSelect({
                autoFocus: true,
                defaultOpen: true,
                onBlur: () => setFindOpen(false),
                onKeyDown: (e: React.KeyboardEvent) => e.key === 'Escape' && setFindOpen(false),
              })}
            </div>
          ) : (
            <Tooltip title={t('clientPage.tools.find')}>
              <Button
                size="small"
                type="text"
                icon={<SearchOutlined />}
                aria-label={t('clientPage.tools.find')}
                onClick={() => setFindOpen(true)}
                data-testid="cc-client-show-find-button"
              />
            </Tooltip>
          )}
        </div>
      </div>
      {rowTwo}
    </nav>
  );
};
